import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { DocumentList } from "@/features/documents/DocumentList";
import { EmailList } from "@/features/documents/EmailList";
import { FilterBar } from "@/features/documents/FilterBar";
import { TopBar } from "@/features/documents/TopBar";
import {
  filterStateFromUrl,
  filterStateToUrl,
  useFilterStore,
} from "@/store/filterStore";
import { useSelectionStore } from "@/store/selectionStore";

export function Root() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Subscribe to each field so the sync effect fires on any change.
  const subject = useFilterStore((s) => s.subject);
  const from = useFilterStore((s) => s.from);
  const documentType = useFilterStore((s) => s.documentType);
  const listTab = useFilterStore((s) => s.listTab);
  const dateFrom = useFilterStore((s) => s.dateFrom);
  const dateTo = useFilterStore((s) => s.dateTo);
  const sort = useFilterStore((s) => s.sort);
  const tradeKey = useFilterStore((s) => s.tradeKey);
  const correctedOnly = useFilterStore((s) => s.correctedOnly);
  const mode = useFilterStore((s) => s.mode);
  const selectedEmailId = useSelectionStore((s) => s.selectedEmailId);

  const hydrated = useRef(false);

  // One-time hydration from URL on mount.
  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    const fromUrl = filterStateFromUrl(searchParams);
    const store = useFilterStore.getState();
    (Object.keys(fromUrl) as (keyof typeof fromUrl)[]).forEach((key) => {
      store.set(key, fromUrl[key] as never);
    });
  }, [searchParams]);

  // Open the email drawer whenever the ?email= param appears (deep links, etc.).
  useEffect(() => {
    const emailFromUrl = searchParams.get("email");
    const selection = useSelectionStore.getState();
    if (emailFromUrl && selection.selectedEmailId !== emailFromUrl) {
      selection.open(emailFromUrl);
    }
  }, [searchParams]);

  // Store → URL sync, driven by value changes (no manual subscribe).
  useEffect(() => {
    if (!hydrated.current) return;
    const next = filterStateToUrl({
      subject,
      from,
      documentType,
      listTab,
      dateFrom,
      dateTo,
      sort,
      tradeKey,
      correctedOnly,
      mode,
    });
    if (selectedEmailId) {
      next.set("email", selectedEmailId);
    }
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [
    subject,
    from,
    documentType,
    listTab,
    dateFrom,
    dateTo,
    sort,
    tradeKey,
    correctedOnly,
    mode,
    selectedEmailId,
    searchParams,
    setSearchParams,
  ]);

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <TopBar />
      <FilterBar />
      <div style={{ flex: 1, overflowY: "auto" }}>
        {mode === "emails" ? <EmailList /> : <DocumentList />}
      </div>
    </div>
  );
}
